/**
 * Renderer dashboard store — a THIN mirror of the main-process
 * DashboardRepository plus UI-only state. All mutations go through IPC to the
 * repository (the sole writer; licensing enforced there — this store never
 * sends a Pro flag). Repository pushes (`dashboards:changed`, incl.
 * MCP-originated mutations) upsert into the mirror via the bridge below.
 */
import { create } from 'zustand';
import type {
  Dashboard,
  DashboardChange,
  DashboardTile,
  RepoResult,
} from '../types/dashboardTypes';

type FreeLimitState = { blocked: true; detail?: string } | null;

type DashboardStoreState = {
  dashboards: Dashboard[];
  warnings: Array<{ file: string; error: string }>;
  loaded: boolean;
  /** Typed free-limit surface for S16 UI (meter, blocked-create tooltip). */
  freeLimit: FreeLimitState;

  load: () => Promise<void>;
  create: (name: string) => Promise<RepoResult<Dashboard>>;
  duplicate: (id: string) => Promise<RepoResult<Dashboard>>;
  importDefinition: (json: string) => Promise<RepoResult<Dashboard>>;
  save: (dashboard: Dashboard, expectedRevision: number) => Promise<RepoResult<Dashboard>>;
  addTile: (dashboardId: string, tile: DashboardTile) => Promise<RepoResult<Dashboard>>;
  updateTile: (dashboardId: string, tile: DashboardTile) => Promise<RepoResult<Dashboard>>;
  removeTile: (dashboardId: string, tileId: string) => Promise<RepoResult<Dashboard>>;
  remove: (id: string) => Promise<RepoResult<void>>;
  clearFreeLimit: () => void;
  applyChange: (change: DashboardChange) => void;
};

const api = () => window.electronAPI?.dashboards;

const unavailable = <T,>(): RepoResult<T> => ({
  ok: false,
  error: 'io',
  detail: 'Dashboards API is unavailable',
});

const upsert = (dashboards: Dashboard[], d: Dashboard): Dashboard[] => {
  const index = dashboards.findIndex((x) => x.id === d.id);
  if (index === -1) return [...dashboards, d];
  const next = dashboards.slice();
  next[index] = d;
  return next;
};

export const useDashboardStore = create<DashboardStoreState>((set, get) => {
  /** Applies a mutation result to the mirror; free-limit surfaces as typed state. */
  const applyResult = (result: RepoResult<Dashboard>): RepoResult<Dashboard> => {
    if (result.ok) {
      set((s) => ({ dashboards: upsert(s.dashboards, result.value) }));
    } else if (result.error === 'free-limit') {
      set({ freeLimit: { blocked: true, detail: result.detail } });
    }
    return result;
  };

  return {
    dashboards: [],
    warnings: [],
    loaded: false,
    freeLimit: null,

    load: async () => {
      const bridge = api();
      if (!bridge) return;
      const { dashboards, warnings } = await bridge.list();
      set({ dashboards, warnings, loaded: true });
    },

    create: async (name) => {
      const bridge = api();
      if (!bridge) return unavailable();
      return applyResult(await bridge.create(name));
    },

    duplicate: async (id) => {
      const bridge = api();
      if (!bridge) return unavailable();
      return applyResult(await bridge.duplicate(id));
    },

    importDefinition: async (json) => {
      const bridge = api();
      if (!bridge) return unavailable();
      return applyResult(await bridge.importDefinition(json));
    },

    save: async (dashboard, expectedRevision) => {
      const bridge = api();
      if (!bridge) return unavailable();
      const result = await bridge.save(dashboard, expectedRevision);
      if (!result.ok && result.error === 'conflict') {
        // Never blind-overwrite: refresh the mirror so the caller can replay
        // its intent against the current revision.
        await get().load();
        return result;
      }
      return applyResult(result);
    },

    addTile: async (dashboardId, tile) => {
      const bridge = api();
      if (!bridge) return unavailable();
      return applyResult(await bridge.addTile(dashboardId, tile));
    },

    updateTile: async (dashboardId, tile) => {
      const bridge = api();
      if (!bridge) return unavailable();
      return applyResult(await bridge.updateTile(dashboardId, tile));
    },

    removeTile: async (dashboardId, tileId) => {
      const bridge = api();
      if (!bridge) return unavailable();
      return applyResult(await bridge.removeTile(dashboardId, tileId));
    },

    remove: async (id) => {
      const bridge = api();
      if (!bridge) return unavailable();
      const result = await bridge.delete(id);
      if (result.ok) {
        set((s) => ({ dashboards: s.dashboards.filter((d) => d.id !== id) }));
      }
      return result;
    },

    clearFreeLimit: () => set({ freeLimit: null }),

    applyChange: (change) => {
      if (change.kind === 'deleted') {
        set((s) => ({ dashboards: s.dashboards.filter((d) => d.id !== change.id) }));
      } else if (change.dashboard) {
        set((s) => {
          // Drop stale AND equal-revision pushes. Stale: broadcasts from a
          // save burst can arrive AFTER the request/response path already
          // applied a newer revision — blind upserts briefly rewind the
          // mirror (visible as tile counts counting up in the switcher).
          // Equal: every request-path mutation is echoed by a broadcast of
          // the SAME revision (the repository bumps `revision` on every
          // write, so equal revision ⇒ identical content); re-upserting it
          // replaces the array + dashboard identity for no data change and
          // re-renders the whole tab subtree — churn that can nudge lingering
          // Radix layers into unmounting mid-interaction (S24 F8).
          const existing = s.dashboards.find((d) => d.id === change.dashboard!.id);
          if (existing && existing.revision >= change.dashboard!.revision) return s;
          return { dashboards: upsert(s.dashboards, change.dashboard!) };
        });
      }
    },
  };
});

/**
 * Subscribes the store to repository pushes. Call once at app boot; returns
 * the unsubscribe. MCP-originated mutations reach the UI through this.
 */
export function initDashboardStoreBridge(): () => void {
  const bridge = api();
  if (!bridge) return () => {};
  return bridge.onChanged((change) => useDashboardStore.getState().applyChange(change));
}
